/**
 * SettingsSaveBar — appears only while something on the page is unsaved.
 * It saves or discards the open section's edits; when the unsaved edits are
 * elsewhere it names that section and offers to go there, rather than show a
 * Save button that would do nothing.
 *
 * Design system: a `surface` bar with a `border` rule, the message in `body`,
 * Discard and Go to as secondary ds Buttons and Save changes as the primary
 * one (44px on a phone).
 */
import React from 'react';
import { textRole, tokenVars, withAlpha } from '@turn-connect/ui';
import { Button } from '../../ui/ds';

/** "Details", "Details and Financials", "Details, Work Orders and Financials". */
export function joinAreas(areas: string[]): string {
  if (areas.length <= 1) return areas[0] ?? '';
  return `${areas.slice(0, -1).join(', ')} and ${areas[areas.length - 1]}`;
}

export interface SettingsSaveBarProps {
  activeLabel: string;
  activeDirty: boolean;
  /** Other sections with unsaved edits, in list order. */
  others: Array<{ id: string; label: string }>;
  saving: boolean;
  canSave: boolean;
  onSave: () => void;
  onDiscard: () => void;
  onGoTo: (sectionId: string) => void;
  touch: boolean;
}

export const SettingsSaveBar: React.FC<SettingsSaveBarProps> = ({
  activeLabel,
  activeDirty,
  others,
  saving,
  canSave,
  onSave,
  onDiscard,
  onGoTo,
  touch,
}) => {
  if (!activeDirty && others.length === 0) return null;
  const buttonStyle: React.CSSProperties = touch ? { flex: 1, whiteSpace: 'nowrap', minHeight: 44 } : { whiteSpace: 'nowrap' };
  const otherNames = joinAreas(others.map((o) => o.label));

  return (
    <div
      role="region"
      aria-label="Unsaved changes"
      data-testid="settings-save-bar"
      style={{
        position: 'sticky',
        bottom: 0,
        zIndex: 2,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '8px 10px',
        padding: touch ? '10px 14px calc(12px + env(safe-area-inset-bottom, 0px))' : '10px 24px',
        background: tokenVars.surface,
        borderTop: `1px solid ${tokenVars.border}`,
        // A soft lift off the page (a third of the backdrop), so it reads in light and dark alike.
        boxShadow: `0 -6px 16px ${withAlpha(tokenVars.overlay, 0.35)}`,
      }}
    >
      <span
        aria-live="polite"
        style={{ ...textRole('body'), flex: touch ? '1 1 100%' : '1 1 200px', minWidth: 0 }}
        data-testid="settings-save-bar-message"
      >
        {activeDirty ? (
          <>
            Unsaved changes in <b style={{ color: tokenVars.textAccent }}>{activeLabel}</b>
          </>
        ) : (
          <>
            Unsaved changes in <b style={{ color: tokenVars.textAccent }}>{otherNames}</b>
          </>
        )}
        {activeDirty && others.length > 0 && (
          <span data-testid="settings-save-bar-others" style={{ ...textRole('meta'), display: 'block' }}>
            Also unsaved: <b style={{ color: tokenVars.text.secondary }}>{otherNames}</b>
          </span>
        )}
      </span>
      {activeDirty ? (
        <>
          <Button variant="secondary" onClick={onDiscard} disabled={saving} data-testid="settings-discard" style={buttonStyle}>
            Discard
          </Button>
          <Button variant="primary" onClick={onSave} disabled={saving || !canSave} data-testid="settings-save" style={buttonStyle}>
            {saving ? 'Saving...' : 'Save changes'}
          </Button>
        </>
      ) : (
        <Button variant="secondary" onClick={() => onGoTo(others[0].id)} data-testid="settings-save-bar-go" style={buttonStyle}>
          Go to {others[0].label}
        </Button>
      )}
    </div>
  );
};

export default SettingsSaveBar;
